import React, { useContext } from 'react';
import { VenueAnalysisData } from 'widgets/venueAnalysis/venueAnalysisAggregatedData.ts';
import { VenueAnalysisConfigurationState } from 'widgets/venueAnalysis/store/configuration/VenueAnalysisConfigurationState.ts';
import AppContext from 'app/AppContext.tsx';
import { safeCalculatePercent } from 'utils/percent.ts';
import Tooltip from 'utils/components/Tooltip/Tooltip.tsx';

import './OrderLabel.scss';

export function OrderLabel({
    data,
    config,
}: {
    data: VenueAnalysisData;
    config: VenueAnalysisConfigurationState;
}) {
    const { numberFormat } = useContext(AppContext);

    if (data.fillInfo === undefined) {
        return <></>;
    }

    const { avgPriceDecimals } = config;

    const buyQty = data.fillInfo?.buyFillQty ?? 0;
    const sellQty = data.fillInfo?.sellFillQty ?? 0;
    const buyRem = data.fillInfo?.buyRemainingQty ?? 0;
    const sellRem = data.fillInfo?.sellRemainingQty ?? 0;

    const totalFilledQty = buyQty + sellQty;
    const totalRem = buyRem + sellRem;
    const qty = totalFilledQty + totalRem;

    const buyVal = (data.fillInfo?.buyFillPrice ?? 0) * buyQty;
    const sellVal = (data.fillInfo?.sellFillPrice ?? 0) * sellQty;
    const avgPrice = totalFilledQty ? (buyVal + sellVal) / totalFilledQty : 0;

    const avgPriceDisplay = numberFormat.n(avgPriceDecimals, avgPrice);

    const totalPercent = safeCalculatePercent(totalFilledQty, qty);

    const bothSides = buyQty > 0 && sellQty > 0;

    let str = bothSides ? 'Traded' : buyQty > 0 ? 'Bought' : 'Sold';
    str += ` ${numberFormat.n0(data?.orderVenue?.qty)} of ${numberFormat.n0(
        qty
    )} (${numberFormat.n0(totalPercent)}%)`;

    const toSideText = (label: string, value: number, qty: number) =>
        `${numberFormat.n0(qty)} ${label} (avg ${numberFormat.n(
            avgPriceDecimals,
            (value ?? 0) / qty
        )})`;

    if (!bothSides) {
        str += ` at an average of ${avgPriceDisplay}`;
    } else {
        str += ` - ${toSideText('bought', buyVal, buyQty)} & ${toSideText(
            'sold',
            sellVal,
            sellQty
        )}`;
    }

    return (
        <Tooltip text={str} neverHide={true} className="OrderLabel">
            {str}
        </Tooltip>
    );
}
